import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, MailCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GlassCard } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // Hide keyboard before submitting to improve UX
    document.activeElement?.blur();
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur, veuillez réessayer.");
      // Re-show keyboard on error by refocusing the email input
      setTimeout(() => document.getElementById('email')?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/logo-192.png" alt="Noor El Bayen" className="w-16 h-16 mx-auto mb-4 rounded-xl" draggable="false" />
          <h1 className="text-2xl font-serif text-text-primary mb-2">Mot de passe oublié</h1>
          <p className="text-text-secondary text-sm">
            Entrez votre email — nous vous enverrons un lien sécurisé.
          </p>
        </div>

        <GlassCard>
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent-confirmation">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <MailCheck className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg font-serif text-text-primary mb-2">Email envoyé</h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                Si <strong>{email}</strong> est associé à un compte, un lien de réinitialisation vient d'arriver dans votre boîte.<br />
                Le lien est valable <strong>1 heure</strong>.
              </p>
              <Link to="/login" className="inline-block mt-5 text-primary hover:underline">
                Retour à la connexion
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {error && (
                <div className="p-3 rounded-lg bg-danger/20 border border-danger/50 text-danger text-sm">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-text-primary">Email</Label>
                <Input
                  id="email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-surface border-border"
                  placeholder="votre@email.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button type="submit" disabled={loading}
                      className="w-full bg-primary text-background hover:bg-primary-hover"
                      data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Envoyer le lien
              </Button>
              <div className="text-center text-sm text-text-secondary">
                <Link to="/login" className="text-primary hover:underline">Retour à la connexion</Link>
              </div>
            </form>
          )}
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
